import { createTheme, Theme } from '@mui/material/styles';

type PaletteMode = 'light' | 'dark';

// Declare module augmentation for custom palette colors
declare module '@mui/material/styles' {
  interface Palette {
    accent: {
      main: string;
      light: string;
      dark: string;
    };
    gradient: {
      primary: string;
      secondary: string;
      accent: string;
      glass: string;
      glassDark: string;
    };
    surface: {
      primary: string;
      secondary: string;
      tertiary: string;
      elevated: string;
    };
  }

  interface PaletteOptions {
    accent?: {
      main: string;
      light: string;
      dark: string;
    };
    gradient?: {
      primary: string;
      secondary: string;
      accent: string;
      glass: string;
      glassDark: string;
    };
    surface?: {
      primary: string;
      secondary: string;
      tertiary: string;
      elevated: string;
    };
  }
}

// Light theme colors
const lightColors = {
  primary: {
    main: '#007AFF', // Apple blue
    light: '#66B2FF',
    dark: '#0056B3',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#E60023', // Pinterest red
    light: '#FF5A79',
    dark: '#B3001A',
    contrastText: '#FFFFFF',
  },
  accent: {
    main: '#8E44AD', // Revolut purple
    light: '#C39BD3',
    dark: '#6C3483',
  },
  background: {
    default: '#F8F9FA',
    paper: '#FFFFFF',
  },
  surface: {
    primary: '#FFFFFF',
    secondary: '#F1F3F4',
    tertiary: '#E8EAED',
    elevated: '#FFFFFF',
  },
  text: {
    primary: '#1D1D1F',
    secondary: '#86868B',
  },
  divider: 'rgba(0, 0, 0, 0.06)',
  gradient: {
    primary: 'linear-gradient(135deg, #007AFF 0%, #5AC8FA 100%)',
    secondary: 'linear-gradient(135deg, #E60023 0%, #FF6B9D 100%)',
    accent: 'linear-gradient(135deg, #8E44AD 0%, #C39BD3 100%)',
    glass: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
    glassDark: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
  },
};

// Dark theme colors
const darkColors = {
  primary: {
    main: '#0A84FF', // iOS 14 blue
    light: '#64B5F6',
    dark: '#1565C0',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#FF375F', // Vibrant pink
    light: '#FF6B9D',
    dark: '#C2185B',
    contrastText: '#FFFFFF',
  },
  accent: {
    main: '#9C27B0', // Material purple
    light: '#CE93D8',
    dark: '#7B1FA2',
  },
  background: {
    default: '#0D1117', // GitHub dark
    paper: '#161B22',
  },
  surface: {
    primary: '#161B22',
    secondary: '#21262D',
    tertiary: '#30363D',
    elevated: '#1C2128',
  },
  text: {
    primary: '#F0F6FC',
    secondary: '#8B949E',
  },
  divider: 'rgba(240, 246, 252, 0.1)',
  gradient: {
    primary: 'linear-gradient(135deg, #0A84FF 0%, #64B5F6 100%)',
    secondary: 'linear-gradient(135deg, #FF375F 0%, #FF6B9D 100%)',
    accent: 'linear-gradient(135deg, #9C27B0 0%, #CE93D8 100%)',
    glass: 'linear-gradient(135deg, rgba(22, 27, 34, 0.95) 0%, rgba(22, 27, 34, 0.8) 100%)',
    glassDark: 'linear-gradient(135deg, rgba(13, 17, 23, 0.95) 0%, rgba(13, 17, 23, 0.8) 100%)',
  },
};

// Shadows for light and dark themes
const lightShadows = [
  'none',
  '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
  '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.06)',
  '0 10px 15px rgba(0, 0, 0, 0.08), 0 4px 6px rgba(0, 0, 0, 0.05)',
  '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
  '0 25px 50px rgba(0, 0, 0, 0.15), 0 10px 20px rgba(0, 0, 0, 0.1)',
  '0 25px 50px rgba(0, 0, 0, 0.2), 0 15px 30px rgba(0, 0, 0, 0.15)',
  '0 30px 60px rgba(0, 0, 0, 0.25), 0 20px 40px rgba(0, 0, 0, 0.2)',
  '0 35px 70px rgba(0, 0, 0, 0.3), 0 25px 50px rgba(0, 0, 0, 0.25)',
  ...Array(16).fill('0 35px 70px rgba(0, 0, 0, 0.3), 0 25px 50px rgba(0, 0, 0, 0.25)'),
] as any;

const darkShadows = [
  'none',
  '0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2)',
  '0 4px 6px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)',
  '0 10px 15px rgba(0, 0, 0, 0.4), 0 4px 6px rgba(0, 0, 0, 0.3)',
  '0 20px 25px rgba(0, 0, 0, 0.5), 0 10px 10px rgba(0, 0, 0, 0.4)',
  '0 25px 50px rgba(0, 0, 0, 0.6), 0 10px 20px rgba(0, 0, 0, 0.5)',
  '0 25px 50px rgba(0, 0, 0, 0.7), 0 15px 30px rgba(0, 0, 0, 0.6)',
  '0 30px 60px rgba(0, 0, 0, 0.8), 0 20px 40px rgba(0, 0, 0, 0.7)',
  '0 35px 70px rgba(0, 0, 0, 0.9), 0 25px 50px rgba(0, 0, 0, 0.8)',
  ...Array(16).fill('0 35px 70px rgba(0, 0, 0, 0.9), 0 25px 50px rgba(0, 0, 0, 0.8)'),
] as any;

// Create theme function
export const createAppTheme = (mode: PaletteMode = 'light'): Theme => {
  const colors = mode === 'light' ? lightColors : darkColors;
  const shadows = mode === 'light' ? lightShadows : darkShadows;
  
  const baseTheme = createTheme({
    palette: {
      mode,
      primary: colors.primary,
      secondary: colors.secondary,
      accent: colors.accent,
      error: {
        main: mode === 'light' ? '#FF3B30' : '#FF6B6B',
        light: mode === 'light' ? '#FF6B6B' : '#FF8A80',
        dark: mode === 'light' ? '#D70015' : '#E53935',
      },
      warning: {
        main: mode === 'light' ? '#FF9500' : '#FFB74D',
        light: mode === 'light' ? '#FFB84D' : '#FFCC02',
        dark: mode === 'light' ? '#CC7700' : '#F57C00',
      },
      info: {
        main: colors.primary.main,
        light: colors.primary.light,
        dark: colors.primary.dark,
      },
      success: {
        main: mode === 'light' ? '#34C759' : '#66BB6A',
        light: mode === 'light' ? '#7ED321' : '#81C784',
        dark: mode === 'light' ? '#248A3D' : '#388E3C',
      },
      background: colors.background,
      surface: colors.surface,
      text: colors.text,
      divider: colors.divider,
      gradient: colors.gradient,
    },
    typography: {
      fontFamily: [
        'SF Pro Display',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Inter',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: {
        fontSize: '2.75rem',
        fontWeight: 700,
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontSize: '2.25rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize: '1.875rem',
        fontWeight: 600,
        lineHeight: 1.25,
        letterSpacing: '-0.01em',
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.35,
      },
      h6: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
        fontWeight: 400,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
        fontWeight: 400,
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: 1.4,
        fontWeight: 500,
        letterSpacing: '0.02em',
      },
    },
    shape: {
      borderRadius: 16,
    },
    spacing: 8,
    shadows,
  });

  // Enhanced theme with component overrides
  return createTheme(baseTheme, {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: mode === 'light'
              ? 'linear-gradient(135deg, #F8F9FA 0%, #E3F2FD 100%)'
              : 'linear-gradient(135deg, #0D1117 0%, #1C2128 100%)',
            backgroundAttachment: 'fixed',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 12,
            fontWeight: 600,
            fontSize: '0.925rem',
            padding: '12px 24px',
            boxShadow: 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: mode === 'light' 
                ? '0 8px 25px rgba(0, 0, 0, 0.15)'
                : '0 8px 25px rgba(0, 0, 0, 0.4)',
            },
          },
          containedPrimary: {
            background: colors.gradient.primary,
            '&:hover': {
              background: colors.gradient.primary,
              filter: 'brightness(1.1)',
            },
          },
          containedSecondary: {
            background: colors.gradient.secondary,
            '&:hover': {
              background: colors.gradient.secondary,
              filter: 'brightness(1.1)',
            },
          },
          outlined: {
            borderWidth: '1.5px',
            borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(240, 246, 252, 0.2)',
            '&:hover': {
              borderWidth: '1.5px',
              backgroundColor: mode === 'light' 
                ? 'rgba(0, 0, 0, 0.02)' 
                : 'rgba(240, 246, 252, 0.05)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            border: mode === 'light' 
              ? '1px solid rgba(0, 0, 0, 0.04)' 
              : '1px solid rgba(240, 246, 252, 0.08)',
            boxShadow: mode === 'light' 
              ? '0 4px 20px rgba(0, 0, 0, 0.06)'
              : '0 4px 20px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            backgroundColor: colors.surface.primary,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: mode === 'light' 
                ? '0 12px 40px rgba(0, 0, 0, 0.12)'
                : '0 12px 40px rgba(0, 0, 0, 0.5)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: mode === 'light' 
              ? '1px solid rgba(0, 0, 0, 0.04)' 
              : '1px solid rgba(240, 246, 252, 0.08)',
            backgroundColor: colors.surface.primary,
          },
          elevation1: {
            boxShadow: mode === 'light' 
              ? '0 4px 20px rgba(0, 0, 0, 0.06)'
              : '0 4px 20px rgba(0, 0, 0, 0.3)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            border: mode === 'light' 
              ? '1px solid rgba(0, 0, 0, 0.04)' 
              : '1px solid rgba(240, 246, 252, 0.08)',
            backgroundColor: colors.surface.primary,
            boxShadow: mode === 'light' 
              ? '0 20px 60px rgba(0, 0, 0, 0.15)'
              : '0 20px 60px rgba(0, 0, 0, 0.6)',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: mode === 'light' 
              ? '1px solid rgba(0, 0, 0, 0.04)' 
              : '1px solid rgba(240, 246, 252, 0.08)',
            backgroundColor: colors.surface.primary,
            boxShadow: mode === 'light' 
              ? '0 8px 32px rgba(0, 0, 0, 0.12)'
              : '0 8px 32px rgba(0, 0, 0, 0.4)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              backgroundColor: mode === 'light' 
                ? 'rgba(255, 255, 255, 0.8)' 
                : 'rgba(22, 27, 34, 0.8)',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.2s ease',
              '&:hover': {
                '& fieldset': {
                  borderColor: colors.primary.light,
                },
              },
              '&.Mui-focused': {
                backgroundColor: mode === 'light' ? '#FFFFFF' : colors.surface.secondary,
                '& fieldset': {
                  borderWidth: '2px',
                },
              },
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' 
              ? 'rgba(255, 255, 255, 0.85)' 
              : 'rgba(13, 17, 23, 0.85)',
            backdropFilter: 'blur(20px)',
            borderBottom: mode === 'light' 
              ? '1px solid rgba(0, 0, 0, 0.06)' 
              : '1px solid rgba(240, 246, 252, 0.1)',
            boxShadow: mode === 'light' 
              ? '0 1px 20px rgba(0, 0, 0, 0.04)'
              : '0 1px 20px rgba(0, 0, 0, 0.3)',
            color: colors.text.primary,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'light' 
              ? 'rgba(255, 255, 255, 0.95)' 
              : 'rgba(22, 27, 34, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRight: mode === 'light' 
              ? '1px solid rgba(0, 0, 0, 0.06)' 
              : '1px solid rgba(240, 246, 252, 0.1)',
            boxShadow: mode === 'light' 
              ? '0 0 40px rgba(0, 0, 0, 0.08)'
              : '0 0 40px rgba(0, 0, 0, 0.4)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            fontWeight: 500,
            fontSize: '0.8rem',
            height: 28,
          },
          filled: {
            '&.MuiChip-colorPrimary': {
              background: colors.gradient.primary,
              color: '#FFFFFF',
            },
            '&.MuiChip-colorSecondary': {
              background: colors.gradient.secondary,
              color: '#FFFFFF',
            },
          },
          outlined: {
            borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(240, 246, 252, 0.2)',
            color: colors.text.primary,
            '&:hover': {
              backgroundColor: mode === 'light' 
                ? 'rgba(0, 0, 0, 0.04)' 
                : 'rgba(240, 246, 252, 0.05)',
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            margin: '2px 0',
            '&:hover': {
              backgroundColor: mode === 'light' 
                ? 'rgba(0, 122, 255, 0.08)' 
                : 'rgba(10, 132, 255, 0.15)',
            },
            '&.Mui-selected': {
              backgroundColor: mode === 'light' 
                ? 'rgba(0, 122, 255, 0.12)' 
                : 'rgba(10, 132, 255, 0.2)',
              '&:hover': {
                backgroundColor: mode === 'light' 
                  ? 'rgba(0, 122, 255, 0.16)' 
                  : 'rgba(10, 132, 255, 0.25)',
              },
            },
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiBox: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: mode === 'light' 
              ? '0 8px 32px rgba(0, 122, 255, 0.3)'
              : '0 8px 32px rgba(10, 132, 255, 0.4)',
            background: colors.gradient.primary,
            '&:hover': {
              background: colors.gradient.primary,
              filter: 'brightness(1.1)',
              transform: 'scale(1.05)',
            },
          },
        },
      },
      MuiSnackbar: {
        styleOverrides: {
          root: {
            '& .MuiAlert-root': {
              borderRadius: 12,
              backgroundColor: mode === 'light' 
                ? colors.surface.primary 
                : colors.surface.secondary,
              color: colors.text.primary,
              border: mode === 'light' 
                ? '1px solid rgba(0, 0, 0, 0.04)' 
                : '1px solid rgba(240, 246, 252, 0.08)',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'light' 
              ? 'rgba(29, 29, 31, 0.9)' 
              : 'rgba(240, 246, 252, 0.9)',
            color: mode === 'light' ? '#FFFFFF' : '#0D1117',
            backdropFilter: 'blur(10px)',
            borderRadius: 8,
            fontSize: '0.8rem',
            fontWeight: 500,
          },
          arrow: {
            color: mode === 'light' 
              ? 'rgba(29, 29, 31, 0.9)' 
              : 'rgba(240, 246, 252, 0.9)',
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: mode === 'light' 
              ? '1px solid rgba(0, 0, 0, 0.12)' 
              : '1px solid rgba(240, 246, 252, 0.2)',
            color: colors.text.primary,
            '&:hover': {
              backgroundColor: mode === 'light' 
                ? 'rgba(0, 0, 0, 0.04)' 
                : 'rgba(240, 246, 252, 0.05)',
            },
            '&.Mui-selected': {
              backgroundColor: colors.primary.main,
              color: colors.primary.contrastText,
              '&:hover': {
                backgroundColor: colors.primary.dark,
              },
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: colors.primary.main,
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: colors.primary.main,
            },
            '& .MuiSwitch-track': {
              backgroundColor: mode === 'light' 
                ? 'rgba(0, 0, 0, 0.38)' 
                : 'rgba(240, 246, 252, 0.38)',
            },
          },
        },
      },
    },
  });
};

// Export both light and dark themes
export const lightTheme = createAppTheme('light');
export const darkTheme = createAppTheme('dark');

// Export the default theme (light)
export const theme = lightTheme;
export default theme; 